import { ComparacaoProfissao } from "../../types";
import { 
  TIPOGRAFIA, 
  CONTAINERS, 
  TAG_CORES, 
  ESPACAMENTO,
  TRANSICOES,
  CORES_SITE
} from "../../data/visualConstants";

type ProfissoesGraficoProps = {
  profissoes: ComparacaoProfissao[];
  nomeEstado: string;
};

const pluralizar = (profissao: string): string => {
  const profissaoLower = profissao.toLowerCase();
  
  if (profissaoLower === 'médico') return 'médicos';
  if (profissaoLower === 'enfermeiro') return 'enfermeiros';
  if (profissaoLower === 'professor') return 'professores';
  
  return profissaoLower + 's';
};

const obterClassificacao = (percentual: number): { tag: string; estiloTag: string } => {
  if (percentual >= 150) {
    return { tag: "Muito acima", estiloTag: TAG_CORES.MUITO_ACIMA };
  } else if (percentual >= 100) {
    return { tag: "Acima", estiloTag: TAG_CORES.ACIMA };
  } else if (percentual >= 80) {
    return { tag: "Próximo", estiloTag: TAG_CORES.PROXIMO };
  } else if (percentual >= 50) {
    return { tag: "Moderado", estiloTag: TAG_CORES.MODERADO };
  } else {
    return { tag: "Distante", estiloTag: TAG_CORES.DISTANTE };
  }
};

const obterCorBarra = (percentual: number): string => {
  if (percentual >= 150) return 'bg-green-600';
  if (percentual >= 100) return 'bg-green-500';
  if (percentual >= 80) return 'bg-yellow-500';
  if (percentual >= 50) return 'bg-orange-500';
  return 'bg-red-500';
};

const InicialProfissao = ({ nome }: { nome: string }) => {
  return (
    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
      <span className="text-base font-bold text-gray-700">
        {nome[0]}
      </span>
    </div>
  );
};

export const ProfissoesGrafico = ({ profissoes, nomeEstado }: ProfissoesGraficoProps) => {
  return (
    <div className={ESPACAMENTO.GAP_MEDIO} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {profissoes.map((profissao) => {
        const { tag, estiloTag } = obterClassificacao(profissao.percentualUsuario);
        const corBarra = obterCorBarra(profissao.percentualUsuario);
        const porcentagemBarra = Math.min(profissao.percentualUsuario, 200);
        
        return (
          <div 
            key={profissao.nome} 
            className={`${CONTAINERS.CARD_HOVER} ${ESPACAMENTO.CARD_PADDING} ${TRANSICOES.PADRAO}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <InicialProfissao nome={profissao.nome} />
                <span className={TIPOGRAFIA.SUBTITULO}>{profissao.nome}</span>
              </div>
              <span className={`${TIPOGRAFIA.TAG_BASE} border-2 ${estiloTag}`}>
                {tag}
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <p className={TIPOGRAFIA.LABEL_PEQUENO}>Salário médio no estado</p>
                <p className={TIPOGRAFIA.VALOR_DESTAQUE_PEQUENO}>
                  R$ {profissao.salarioMedio.toLocaleString('pt-BR')}
                </p>
              </div>
              <div>
                <p className={TIPOGRAFIA.LABEL_PEQUENO}>Seu salário representa</p>
                <p className="text-lg font-bold" style={{ color: CORES_SITE.VERDE_SUCESSO }}>
                  {profissao.percentualUsuario}%
                </p>
                <p className={TIPOGRAFIA.MICRO}>do salário desta profissão</p>
              </div>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-3">
              <div 
                className={`h-2.5 rounded-full ${corBarra} ${TRANSICOES.SUAVE}`}
                style={{ 
                  width: `${Math.min(porcentagemBarra / 2, 100)}%`
                }}
              />
            </div>
            
            <p className={TIPOGRAFIA.CORPO_SECUNDARIO}>
              {profissao.percentualUsuario >= 100 
                ? `Você ganha ${profissao.percentualUsuario - 100}% a mais que a média dos ${pluralizar(profissao.nome)} no ${nomeEstado}`
                : `Faltam ${100 - profissao.percentualUsuario}% para alcançar a média dos ${pluralizar(profissao.nome)} no ${nomeEstado}`
              }
            </p>
          </div>
        );
      })}
    </div>
  );
};